/**
 * ResetEventButton — SuperAdmin danger-zone control that wipes runtime
 * event activity while preserving guests, inventory, tables, menu, and
 * accounts. Calls POST /api/admin/event/reset.
 */
import { useState } from "react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, X } from "lucide-react";

const DANGER = "#dc2626";

export default function ResetEventButton() {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastCounts, setLastCounts] = useState(null);

  const doReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/event/reset", { reason });
      setLastCounts(data.counts);
      const total = Object.values(data.counts || {}).reduce((a, b) => a + b, 0);
      toast.success(`Event reset · ${total} live records cleared`);
      setConfirming(false);
      setReason("");
      setAck("");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="card-elev p-5 mt-6"
      style={{ border: `1px solid ${DANGER}40`, background: `${DANGER}08` }}
      data-testid="admin-danger-zone"
    >
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4" style={{ color: DANGER }} />
        <div className="font-display text-lg" style={{ color: DANGER }}>
          Danger zone
        </div>
      </div>
      <p className="text-sm text-motif-muted mb-4 leading-snug">
        Clear all live event activity — requests, queues, feed, alerts, games,
        crossword & Who-Will state, and push deliveries. Guests, tables,
        inventory, menu, staff accounts, and settings are all preserved.
        This action is audited.
      </p>

      <button
        onClick={() => setConfirming(true)}
        className="w-full px-4 py-3 rounded-xl font-medium text-white shadow-sm transition hover:opacity-90"
        style={{ background: DANGER }}
        data-testid="admin-reset-event-btn"
      >
        RESET EVENT
      </button>

      {lastCounts && (
        <div className="mt-3 text-[11px] text-motif-muted">
          Last reset removed: {Object.entries(lastCounts).filter(([, n]) => n > 0).map(([k, n]) => `${k} (${n})`).join(", ") || "nothing"}.
        </div>
      )}

      {confirming && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => !busy && setConfirming(false)}
          role="dialog"
          aria-modal="true"
          data-testid="admin-reset-confirm"
        >
          <div
            className="w-full max-w-md rounded-2xl shadow-2xl slide-up"
            style={{ background: "var(--motif-surface)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 relative">
              <button
                onClick={() => !busy && setConfirming(false)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: "var(--motif-accent)" }}
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5" style={{ color: DANGER }} />
                <h3 className="font-display text-xl" style={{ color: DANGER }}>
                  Reset event?
                </h3>
              </div>
              <p className="text-sm text-motif leading-snug mb-4">
                This will permanently remove all live event activity while
                preserving guests, configuration, inventory and settings.
              </p>

              <label className="text-[10px] tracking-luxe text-motif-muted block mb-1">
                REASON (optional)
              </label>
              <input
                className="input-motif mb-3"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Rehearsal cleanup"
                data-testid="admin-reset-reason"
              />

              <label className="text-[10px] tracking-luxe text-motif-muted block mb-1">
                Type <span style={{ color: DANGER }}>RESET</span> to confirm
              </label>
              <input
                className="input-motif"
                value={ack}
                onChange={(e) => setAck(e.target.value)}
                placeholder="RESET"
                data-testid="admin-reset-ack"
                autoComplete="off"
              />
            </div>
            <div className="p-4 border-t flex gap-2" style={{ borderColor: "var(--motif-border)" }}>
              <button
                onClick={() => setConfirming(false)}
                disabled={busy}
                className="btn-ghost text-sm flex-1"
                data-testid="admin-reset-cancel"
              >
                Cancel
              </button>
              <button
                onClick={doReset}
                disabled={busy || ack.trim() !== "RESET"}
                className="text-sm flex-1 px-4 py-2 rounded-lg font-medium text-white disabled:opacity-40"
                style={{ background: DANGER }}
                data-testid="admin-reset-confirm-btn"
              >
                {busy ? "Resetting…" : "Yes, reset event"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
